import { CacheTypes } from "../cache/interfaces";
import { cacheUtils } from "./../../index";
// class to handle appointment date changes, time of day changes
// it will determine if the appointment date is valid and if the time of day is valid
// if the appointment date is valid, it will set the appointment date to the time of day
// if the time of day is valid, it will set the time of day to the appointment date
// if both are valid, it will set the appointment date and time of day to the appointment date and time of day
// if both are invalid, it will set the appointment date and time of day to the current date and time of day
// it will determine if appointment is today
// it will determine if appointment is in the past
// it will determine if appointment is in the future
// it will determine if time of day is before 9am or after and before noon or after 3pm
// it will determine if time of day is am or pm
// it will determine what appointment time of day is available based on the current time from 3 options (am, pm, eve)

interface AppointmentDefaults {
  appointmentTimeTypeId: number;
  name: string;
  timeStart: string;
  timeEnd: string;
  defaultTime: string;
  careClubEnabled: boolean;
}

export class AppointmentDate {
  public static isUTC = true;

  public static convertToDate = (date: any): Date => {
    // handle string, number and date
    if (typeof date === "string") {
      return new Date(date);
    } else if (typeof date === "number") {
      return new Date(date);
    } else if (date instanceof Date) {
      return date;
    } else {
      return new Date();
    }
  };

  public static getAppointmentDefaultSettings() {
    const appointmentTimeTypes = cacheUtils.getCache<CacheTypes.AppointmentTimeTypes>("AppointmentTimeTypes");
    return appointmentTimeTypes;
  }

  public static findAppointmentTime(appointmentTime: "am" | "pm" | "eve"): AppointmentDefaults | null {
    const appointmentTimeTypes = AppointmentDate.getAppointmentDefaultSettings();
    if (appointmentTimeTypes && appointmentTimeTypes.length > 0) {
      const response = appointmentTimeTypes.find((option: { name: string }) => {
        return option.name === appointmentTime.toUpperCase();
      });
      return response ? response : null;
    }
    return null;
  }

  public static setDateToTimeOfDay(date: Date, timeOfDay: "am" | "pm" | "eve"): Date {
    // get appointment time type from appointment time types
    const appointmentTimeType = AppointmentDate.findAppointmentTime(timeOfDay);

    // if appointment time type exists
    if (appointmentTimeType) {
      const hours = parseInt(appointmentTimeType.defaultTime.split(":")[0], 10);
      const minutes = parseInt(appointmentTimeType.defaultTime.split(":")[1], 10);
      const seconds = parseInt(appointmentTimeType.defaultTime.split(":")[2], 10);

      const timeOfDayHours = hours;
      const timeOfDayMinutes = minutes;
      const timeOfDaySeconds = seconds;
      const timeOfDayMilliseconds = 0;

      // set date to appointment time type
      if (timeOfDayHours && timeOfDayMinutes !== null && timeOfDaySeconds !== null && timeOfDayMilliseconds !== null) {
        // if (AppointmentDate.isUTC) {
        //   date.setUTCHours(timeOfDayHours, timeOfDayMinutes, timeOfDaySeconds, timeOfDayMilliseconds);
        // } else {
        date.setHours(timeOfDayHours, timeOfDayMinutes, timeOfDaySeconds, timeOfDayMilliseconds);
        // }
      }
    }

    // return date
    return date;
  }

  // check if appointment date is today
  public static checkAppointmentDateIsToday(appointmentDate: Date): boolean {
    const today = new Date().toISOString().split("T")[0];
    return today === appointmentDate?.toISOString().split("T")[0];
  }

  // get earliest appointment time of day
  public static getEarliestAppointmentTime(appointmentDate: Date): "am" | "pm" | "eve" {
    let appointmentTime: "am" | "pm" | "eve" = "am";
    // check if date today
    if (AppointmentDate.checkAppointmentDateIsToday(appointmentDate)) {
      // get time of day from appointment time types
      appointmentTime = AppointmentDate.checkAppointmentTime();
    }

    // return time of day
    return appointmentTime;
  }

  // get earliest appointment time of day for emergency
  public static getEarliestAppointmentTimeForEmergency(appointmentDate: Date): "am" | "pm" | "eve" {
    let appointmentTime: "am" | "pm" | "eve" = "am";
    // check if date today
    if (AppointmentDate.checkAppointmentDateIsToday(appointmentDate)) {
      // get time of day from appointment time types
      appointmentTime = AppointmentDate.checkAppointmentTimeForEmergency();
    }

    // return time of day
    return appointmentTime;
  }

  // get valid am pm eve options for current time
  public static getValidAmPmEveOptions(appointmentDate: Date | null): { am: boolean; pm: boolean; eve: boolean } {
    // get time of day from appointment time types

    if (appointmentDate) {
      // if date is not today
      if (!AppointmentDate.checkAppointmentDateIsToday(appointmentDate)) {
        // set time of day to am
        return { am: true, pm: true, eve: true };
      } else {
        const appointmentTime = AppointmentDate.getEarliestAppointmentTime(appointmentDate);
        if (appointmentTime === "am") {
          return {
            am: true,
            pm: true,
            eve: true,
          };
        }
        if (appointmentTime === "pm") {
          return {
            am: false,
            pm: true,
            eve: true,
          };
        }
        if (appointmentTime === "eve") {
          return {
            am: false,
            pm: false,
            eve: true,
          };
        }
      }
    }
    return {
      am: false,
      pm: false,
      eve: false,
    };
  }

  // get valid am pm eve options for emergency
  public static getValidAmPmEveOptionsForEmergency(): { am: boolean; pm: boolean; eve: boolean } {
    // get time of day from appointment time types
    const appointmentDate = new Date();
    {
      const appointmentTime = AppointmentDate.getEarliestAppointmentTimeForEmergency(appointmentDate);
      if (appointmentTime === "am") {
        return {
          am: true,
          pm: true,
          eve: true,
        };
      }
      if (appointmentTime === "pm") {
        return {
          am: false,
          pm: true,
          eve: true,
        };
      }
      if (appointmentTime === "eve") {
        return {
          am: false,
          pm: false,
          eve: true,
        };
      }
    }
    return {
      am: false,
      pm: false,
      eve: false,
    };
  }

  public static getValidTimeForDate(appointmentDate: Date | null): "am" | "pm" | "eve" {
    // get time of day from appointment time types
    if (appointmentDate) {
      const appointmentTime = AppointmentDate.getEarliestAppointmentTime(appointmentDate);
      if (appointmentTime === "am") {
        return "am";
      }
      if (appointmentTime === "pm") {
        return "pm";
      }
      if (appointmentTime === "eve") {
        return "eve";
      }
    }
    return "am";
  }

  // change timeofday
  public static changeToValidTimeForDate(appointmentDate: Date | null, timeOfDay: "am" | "pm" | "eve", emergency: boolean = false): Date {
    appointmentDate = appointmentDate || new Date();
    // check if time of day is valid for current date, only if it is today
    if (AppointmentDate.checkAppointmentDateIsToday(appointmentDate)) {
      // check if time of day is valid for current date
      const options = emergency ? AppointmentDate.getValidAmPmEveOptionsForEmergency() : AppointmentDate.getValidAmPmEveOptions(appointmentDate);
      if (options[timeOfDay]) {
        // set time of day
        return AppointmentDate.setDateToTimeOfDay(appointmentDate, timeOfDay);
      } else {
        // set time of day to am
        return AppointmentDate.setDateToTimeOfDay(appointmentDate, Object.keys(options)[0] as "am" | "pm" | "eve");
      }
    } else {
      // set time of day
      return AppointmentDate.setDateToTimeOfDay(appointmentDate, timeOfDay);
    }
  }

  // set default appointment date with hours for time of day
  public static setDefaultsForDate(appointmentDate: Date, appointmentTime: "am" | "pm" | "eve"): Date {
    // get appointment time type from appointment time types for earliest appointment time of day
    const appointmentTimeType = AppointmentDate.findAppointmentTime(appointmentTime);

    // if appointment time type exists
    if (appointmentTimeType) {
      const hours = parseInt(appointmentTimeType.defaultTime.split(":")[0], 10);
      const minutes = parseInt(appointmentTimeType.defaultTime.split(":")[1], 10);
      const seconds = parseInt(appointmentTimeType.defaultTime.split(":")[2], 10);

      const timeOfDayHours = hours;
      const timeOfDayMinutes = minutes;
      const timeOfDaySeconds = seconds;
      const timeOfDayMilliseconds = 0;


      // set date to appointment time type
      if (timeOfDayHours && timeOfDayMinutes !== null && timeOfDaySeconds !== null && timeOfDayMilliseconds !== null) {
        // set appointment date to current date
        appointmentDate.setHours(timeOfDayHours, timeOfDayMinutes, timeOfDaySeconds, timeOfDayMilliseconds);
        // if (AppointmentDate.isUTC) {
        //   appointmentDate = AppointmentDate.convertToUTC(appointmentDate);
        // }
      }
      return appointmentDate;
    }
    return appointmentDate;
  }

  // public static convertToUTC(appointmentDate: Date): Date {
  //   const offset = appointmentDate.getTimezoneOffset() * 60 * 1000;
  //   return new Date(appointmentDate.getTime() - offset);
  // }

  // set earliest appointment time of day
  public static setEarliestAppointmentTime(appointmentDate: Date): Date {
    // get earliest appointment time of day
    const earliestAppointmentTime = AppointmentDate.getEarliestAppointmentTime(appointmentDate); // check if date today or not today then set to am or pm or eve time of day, if not today then set to am

    appointmentDate = AppointmentDate.setDefaultsForDate(appointmentDate, earliestAppointmentTime);

    // set time of day
    return appointmentDate;
  }

  // set earliest appointment time of day for emergency
  public static setEarliestAppointmentTimeForEmergency(appointmentDate: Date): Date {
    // get earliest appointment time of day
    const earliestAppointmentTime = AppointmentDate.getEarliestAppointmentTimeForEmergency(appointmentDate); // check if date today or not today then set to am or pm or eve time of day, if not today then set to am

    // get appointment time type from appointment time types for earliest appointment time of day
    appointmentDate = AppointmentDate.setDefaultsForDate(appointmentDate, earliestAppointmentTime);

    // set time of day
    return appointmentDate;
  }

  public static checkAppointmentTimeForEmergency(): "am" | "pm" | "eve" {
    // set default time of day based on current time and available time of day
    const hourNow = new Date().getHours();
    console.log("hourNow", hourNow);
    // if current time is 15:00 or later, set default time of day to evening
    switch (true) {
      case hourNow >= 16:
        return "eve";

      // if current time is 9:00 or later, set default time of day to afternoon
      case hourNow >= 11:
        return "pm";

      // if current time is before 9:00, set default time of day to morning
      default:
        return "am";
    }
  }

  // check if appointment date is am pm or eve
  public static checkAppointmentTime(): "am" | "pm" | "eve" {
    // set default time of day based on current time and available time of day
    const hourNow = new Date().getHours();

    // if current time is 15:00:00 or later, set default time of day to evening
    switch (true) {
      case hourNow >= 15:
        return "eve";

      // if current time is 9:00 or later, set default time of day to afternoon
      case hourNow >= 9:
        return "pm";

      // if current time is before 9:00, set default time of day to morning
      default:
        return "am";
    }
  }

  // get time range for appointment time of day
  public static getTimeRangeForAppointments(appointmentTime: "am" | "pm" | "eve") {
    // get appointment time type from appointment time types for earliest appointment time of day
    const result = cacheUtils
      .getCache<CacheTypes.AppointmentTimeTypes>("AppointmentTimeTypes")
      ?.find((appointmentTimeType) => appointmentTimeType.name.toLowerCase() === appointmentTime);
    return { timeStart: result?.timeStart, timeEnd: result?.timeEnd };
  }

  // get timeplanned for appointment time of day
  public static getTimeRequirementType(
    emergency: boolean,
    appointmentDate: Date | null
  ): {
    id: number;
    timeRequirementTypeId: number;
    name: string;
    colour: string;
  } {
    // if emergency return { id: 0, timeRequirementTypeId: 0, name: "Emergency", colour: "F5A018" }
    if (emergency) {
      return { id: 0, timeRequirementTypeId: 0, name: "Emergency", colour: "F5A018" };
    }
    // is today
    if (AppointmentDate.checkAppointmentDateIsToday(appointmentDate!)) {
      return { id: 1, timeRequirementTypeId: 1, name: "Same Day", colour: "F5A018" };
    }
    return { id: 2, timeRequirementTypeId: 2, name: "Planned", colour: "28B023" };
  }
}

// declare appointment date variable on window
// window.appointmentDate = appointmentDate;

declare global {
  interface Window {
    AppointmentDate: AppointmentDate;
  }
}

window.AppointmentDate = AppointmentDate;

export default AppointmentDate; // for testing
